import {useEffect, useMemo} from 'react';
import SendingPopup from '../../../component/sendingPopup/SendingPopup';
import {useSelector} from 'react-redux';
import {TState} from '../../../redux/store.type';
import {TShow} from '../../../component/prompotionPopup/PromotionPopup';
import useRestartXHR from '../../../hooks/useRestartXHR';
import {createSendingProps} from '../../../component/sendingPopup/services/createSendingProps';
import {useSendData} from '../../../hooks/useSendData';
import {useTranslation} from 'react-i18next';
import {getCrmRecipients, sendCrmMessage} from '../../../redux/crm/crmActions';
import {useSend} from '../../../component/sendingPopup/hooks/useSend';
import {saveMailing} from '../../../redux/mailing/mailingActions';
import {TabType} from '../../../component/sendingPopup/types';

function CrmSendingPopup({show}: {show: TShow}) {
    const sendingXHR = useSelector((state: TState) => state.crm.sendCrmMessageXHR);
    const crmRecipients = useSelector((state: TState) => state.crm.crmRecipients);
    const crmSendingType = useSelector((state: TState) => state.ui.crmSendingType);
    const hasWhatsappPermission = useSelector((state: TState) => state.account.permissions?.find(p => p.permission === 'whatsApp')?.status === 1);
    const queryFilter = useSelector((state: TState) => state.crm.queryFilter);
    const freeFilter = useSelector((state: TState) => state.crm.freeFilter);
    const status = useSelector((state: TState) => state.crm.status);
    const crmRecipientsList = useSelector((state: TState) => state.crm.crmRecipientsList);
    const hasExplicitList = Array.isArray(crmRecipientsList) && crmRecipientsList.length > 0;
    const eventType = useSelector((state: TState) => state.event.type);
    const eid = useSelector((state: TState) => state.event.eid);
    const {t} = useTranslation('mailing');
    const allowedList = [...(hasWhatsappPermission ? ['whatsapp'] : []), 'email', 'sms'];

    const isGivechak = eventType === 'givechak';
    const translationKeys = isGivechak ? {
        defaultMessage: 'mailingCrmWhatsappGivechak',
        defaultSubject: 'mailingCrmSubjectGivechak',
        defaultEmailBody: 'mailingCrmEmailGivechak',
        defaultSmsMessage: 'mailingCrmSmsGivechak',
    } : {
        defaultMessage: 'mailingCrmWhatsapp',
        defaultSubject: 'mailingCrmSubject',
        defaultEmailBody: 'mailingCrmDefaultText',
        defaultSmsMessage: 'mailingCrmDefaultText',
    };

    // Custom hooks for data management and send logic
    const {whatsappData, emailData, smsData, handleDataSave, whatsappTemplate} = useSendData({
        defaultSubject: t(translationKeys.defaultSubject),
        defaultEmailBody: t(translationKeys.defaultEmailBody),
        defaultSmsMessage: t(translationKeys.defaultSmsMessage),
        type: 'crm',
    });
    const saveData = useMemo(() => ({source: 'crm', eid}), [eid]);
    const {handleSend, handleSave} = useSend({whatsappData, emailData, smsData, show, sendFunction: sendCrmMessage, saveFunction: saveMailing, saveData});

    // XHR restart hook
    useRestartXHR(sendingXHR, 'crm.sendCrmMessageXHR');

    // Create props using utility function
    const {whatsappProps, emailProps, smsProps} = createSendingProps({
        whatsappData,
        emailData,
        smsData,
        handleDataSave,
        handleSend,
        handleSave,
        sendingXHR,
        whatsappTemplate,
        variablesType: 'crm',
        mailingSource: 'crm',
    });

    useEffect(() => {
        // explicit recipient list (e.g. tnuva potentials) — no orders query needed
        if(hasExplicitList) return;
        getCrmRecipients().then();
    }, [queryFilter, freeFilter, status, hasExplicitList]);

    const initialTab = crmSendingType as TabType;

    return (
        <>
            {show.isOpen && whatsappTemplate && (
                <SendingPopup
                    show={show}
                    whatsappProps={whatsappProps}
                    smsProps={smsProps}
                    emailProps={emailProps}
                    amount={hasExplicitList ? crmRecipientsList.length : crmRecipients}
                    amountText={'transactions'}
                    variablesType={'crm'}
                    allowedList={allowedList}
                    initialTab={initialTab}
                />
            )}
        </>
    );
}

export default CrmSendingPopup;
